/**
 * Shared filter option lists for the browse pages (DesignerPage + CollectionPage).
 *
 * SINGLE SOURCE OF TRUTH: both pages import these, so their shared filters can
 * never drift apart again. Page-specific sections stay in each page — the
 * Collection page adds a "Designer" filter, the Designer page adds a
 * "Collection" filter.
 *
 * IMPORTANT: every `value` here must match the exact publicData enum value a
 * listing stores (see src/config/configListing.js), or the filter will match
 * nothing. Labels are the shopper-facing short forms.
 */

import { getListingEnumOptions } from './configListing';

export const PRICE_MIN_DEFAULT = 0;
export const PRICE_MAX_DEFAULT = 5000;

// Only used by the Designer page (its Collection filter).
export const COLLECTION_OPTIONS = [
  { value: 'bridal', label: 'Bridal' },
  { value: 'formals', label: 'Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'mens', label: 'Mens' },
  { value: 'kids', label: 'Kids' },
];

export const COLOR_OPTIONS = [
  { value: 'red-crimson', label: 'Red' },
  { value: 'pink-fuchsia', label: 'Pink' },
  { value: 'blush-baby-pink', label: 'Blush / Baby Pink' },
  { value: 'yellow-mustard-gold', label: 'Gold / Yellow' },
  { value: 'white-ivory', label: 'Ivory / White' },
  { value: 'cream-beige-nude', label: 'Cream / Beige' },
  { value: 'green-emerald', label: 'Green' },
  { value: 'blue', label: 'Blue' },
  { value: 'blue-royal-cobalt', label: 'Royal Blue' },
  { value: 'black', label: 'Black' },
  { value: 'maroon-burgundy-wine', label: 'Maroon' },
  { value: 'purple-plum-mauve', label: 'Purple' },
  { value: 'multi-color-printed', label: 'Multi' },
];

export const SIZE_OPTIONS = [
  { value: 'xs', label: 'XS' },
  { value: 's', label: 'S' },
  { value: 'm', label: 'M' },
  { value: 'l', label: 'L' },
  { value: 'xl', label: 'XL' },
  { value: 'made-to-measure', label: 'Made-to-Measure' },
];

export const FABRIC_OPTIONS = [
  { value: 'silk-pure-silk', label: 'Silk' },
  { value: 'chiffon', label: 'Chiffon' },
  { value: 'organza', label: 'Organza' },
  { value: 'velvet', label: 'Velvet' },
  { value: 'net-tulle', label: 'Net' },
  { value: 'georgette', label: 'Georgette' },
  { value: 'lawn', label: 'Lawn' },
  { value: 'linen', label: 'Linen' },
  { value: 'cotton', label: 'Cotton' },
];

// Derived from configListing so a garment type added to the submission step
// cannot be sellable-but-unfilterable. Adding one there makes it appear in
// every "What's Included" filter automatically - DesignerPage, CollectionPage
// and Browse All.
//
// INCLUDED_LABEL_OVERRIDES carries the shopper-facing short forms where they
// differ from the seller-facing label. INCLUDED_ORDER is display order only:
// anything not listed there still appears, at the end, so a forgotten entry
// degrades to "shown last" rather than "silently missing".
const INCLUDED_LABEL_OVERRIDES = {
  kameez: 'Kameez / Top',
  cholee: 'Blouse / Choli',
  shalwaar: 'Sharara / Shalwaar',
};

const INCLUDED_ORDER = [
  'lehenga',
  'kameez',
  'kurta',
  'sherwani',
  'nehru-jacket',
  'waistcoat',
  'dupatta',
  'cholee',
  'shalwaar',
  'kaftan',
  'shawl',
  'turban',
  'pajama',
  'under-shirt',
];

export const INCLUDED_OPTIONS = getListingEnumOptions('garmentType')
  .map(({ option, label }) => ({
    value: option,
    label: INCLUDED_LABEL_OVERRIDES[option] || label,
  }))
  .sort((a, b) => {
    const ia = INCLUDED_ORDER.indexOf(a.value);
    const ib = INCLUDED_ORDER.indexOf(b.value);
    return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
  });
